<script setup lang="ts">
import { addYears } from 'date-fns'
import { fetchAvailabilityData, queryKey, type InfoData } from '~/queries'

type Props = {
  product: Exclude<InfoData['product'], undefined>
  class?: string
}

const props = defineProps<Props>()

const classes = computed(() => ({
  'app-tickets-transport': true,
  [`${props.class}`]: !!props.class,
}))

const { t } = useI18n()

const cart = useCartStore()

const ticketTypes = [
  { label: t('string.Open-return'), value: 'openReturn' },
  { label: t('string.One-way'), value: 'oneWay' },
] as const

const ticketType = ref<'openReturn' | 'return' | 'oneWay'>('openReturn')

const direction = ref<'toHeathrow' | 'fromHeathrow'>('fromHeathrow')

const minDate = computed(() => new Date())
const maxDate = computed(() => addYears(new Date(), 1))

const date = ref<Date | null>(null)
const returnDate = ref<Date | null>(null)

const isoDate = computed(() => date.value?.toISOString().split('T')[0])
const isoReturnDate = computed(
  () => returnDate.value?.toISOString().split('T')[0],
)

const participants = [
  {
    title: t('string.Adults'),
    description: `16+ ${t('string.years')}`,
    key: 'adults',
  },
  {
    title: t('string.Children'),
    description: `0-15 ${t('string.years')}`,
    key: 'children',
  },
]

const quantity = ref<Record<string, number>>({
  adults: 2,
  children: 0,
})

const maxHeathrowParticipants = 9
const availableParticipants = computed(() => {
  return maxHeathrowParticipants - Object.values(quantity.value).reduce((acc, curr) => acc + curr, 0)
})

const fareId = ref<string | null>(null)

const fare = computed(() =>
  data.value?.items.find((item) => item.variationId === fareId.value),
)

const isValid = computed(
  () =>
    !!(
      ticketType.value &&
      direction.value &&
      Object.values(quantity.value).some((value) => value > 0) &&
      isoDate.value &&
      (ticketType.value === 'return' ? isoReturnDate.value : true)
    ),
)

const { data, pending, refresh, clear } = await useAsyncData(
  queryKey(
    'availability',
    props.product?.id,
    ticketType.value,
    direction.value,
    quantity.value,
    isoDate.value,
    isoReturnDate.value,
  ),
  async () => {
    if (!isValid.value) return

    console.log('fetching availability data', isValid.value)

    const query: Record<string, unknown> = {}

    Object.entries(quantity.value).forEach(([key, value]) => {
      if (value && value > 0) {
        query[`search[${key}]`] = value
      }
    })

    if (ticketType.value === 'openReturn') {
      query['search[openReturn]'] = true
    } else {
      query['search[openReturn]'] = false

      if (ticketType.value === 'return') {
        query['search[returnDate]'] = isoReturnDate.value
      }
    }

    const data = await fetchAvailabilityData({
      productId: props.product.id,
      date: isoDate.value,
      'search[direction]': direction.value,
      ...query,
    })

    return data
  },
  { server: false, lazy: true },
)

const toggleDirection = () => {
  direction.value =
    direction.value === 'fromHeathrow' ? 'toHeathrow' : 'fromHeathrow'
}

const fromLabel = computed(() => {
  return direction.value === 'fromHeathrow'
    ? 'Heathrow Rail'
    : 'London Paddington'
})

const toLabel = computed(() => {
  return direction.value === 'fromHeathrow'
    ? 'London Paddington'
    : 'Heathrow Rail'
})

watch(
  [ticketType, direction, quantity, isoDate, isoReturnDate],
  () => {
    fareId.value = null
    clear()
  },
  { deep: true },
)

watch([date, ticketType], () => (returnDate.value = null))

const submitting = ref(false)
</script>

<template>
  <AppPaper :class="classes" class="relative p-3 pt-6 md:p-16">
    <h3>{{ $t('component.tickets.transport.type.label') }}</h3>

    <AppButtonGroup class="mb-4">
      <AppButton
        v-for="{ label, value } in ticketTypes"
        :key="value"
        variant="outlined"
        size="small"
        :active="ticketType === value"
        @click="ticketType = value"
      >
        {{ label }}
      </AppButton>
    </AppButtonGroup>

    <section class="app-tickets-transport__direction mb-6">
      <p>
        <strong>{{ $t('string.From') }}:</strong>
        <span>{{ fromLabel }}</span>
      </p>

      <p>
        <strong>{{ $t('string.To') }}:</strong>
        <span>{{ toLabel }}</span>
      </p>

      <AppButtonIconRounded
        class="absolute right-2 top-1/2 -translate-y-1/2"
        @click="toggleDirection"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 31">
          <path
            d="M14.725,21.613l-5.729,5.57l-5.73,-5.57"
            style="
              fill: none;
              fill-rule: nonzero;
              stroke: currentColor;
              stroke-width: 2px;
            "
          />
          <path
            d="M9,26.68l0,-11.88"
            style="
              fill: none;
              fill-rule: nonzero;
              stroke: currentColor;
              stroke-width: 2px;
            "
          />
          <path
            d="M11.275,9.387l5.729,-5.57l5.73,5.57"
            style="
              fill: none;
              fill-rule: nonzero;
              stroke: currentColor;
              stroke-width: 2px;
            "
          />
          <path
            d="M17,4.32l0,11.88"
            style="
              fill: none;
              fill-rule: nonzero;
              stroke: currentColor;
              stroke-width: 2px;
            "
          />
        </svg>
      </AppButtonIconRounded>
    </section>

    <h3>{{ $t('component.tickets.transport.date.label') }}</h3>

    <div class="mb-6 flex flex-col items-center sm:flex-row">
      <AppSelectDate
        v-model="date"
        :label="$t('string.Add-date')"
        full-width
        :min-date="minDate"
        :max-date="maxDate"
      />

      <template v-if="ticketType === 'return'">
        <span class="m-3 h-px w-3 flex-shrink-0 bg-gray-300"></span>

        <AppSelectDate
          v-model="returnDate"
          :label="$t('string.Add-return-date')"
          full-width
          :min-date="date || minDate"
          :max-date="maxDate"
          :class="{ 'pointer-events-none opacity-50': !date }"
        />
      </template>
    </div>

    <h3>{{ $t('component.tickets.transport.participants.label') }}</h3>

    <section class="mb-6">
      <header>
        <p>{{ $t('component.tickets.transport.participants.description') }}</p>
      </header>

      <div class="p-4">
        <template
          v-for="(participant, index) in participants"
          :key="participant.key"
        >
          <article class="app-tickets-attraction__category">
            <header>
              <h4>{{ participant.title }}</h4>

              <p>{{ participant.description }}</p>
            </header>

            <p class="app-tickets-attraction__category__price"></p>

            <AppInputQuantity
              v-model="quantity[participant.key]"
              :min="participant.key === 'adults' ? 1 : 0"
              :max="quantity[participant.key] + availableParticipants"
            />
          </article>

          <AppDivider v-if="index < participants.length - 1" class="my-3" />
        </template>
      </div>
    </section>

    <template v-if="data?.items && data?.items.length > 0">
      <h3>Fare</h3>

      <AppRadioGroup
        v-model="fareId"
        :items="
          data.items.map((item) => ({
            label: `${item.ticketClass ? $t(item.ticketClass) : item.variationId} - <strong>${formatCurrency(item.price)}</strong>`,
            value: item.variationId!,
          }))
        "
      />
    </template>

    <AppButton
      full-width
      class="mt-8"
      :disabled="!isValid"
      :loading="submitting"
      @click="
        async () => {
          if (!fare) {
            refresh()
            return
          }

          submitting = true

          await cart.addToCart({
            productId: props.product.id,
            variations: [
              { name: fare.name, fare_id: fare.variationId, quantity: 1 },
            ],
          })

          submitting = false
        }
      "
    >
      {{
        fare
          ? `${$t('component.tickets.transport.button.submit')} (${formatCurrency(fare.price)})`
          : $t('component.tickets.transport.button.get-availability')
      }}
    </AppButton>

    <Transition name="fade">
      <div
        v-if="pending"
        class="text-red absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-white/75"
      >
        <AppSpinner :style="{ fontSize: '2.5rem' }" />
      </div>
    </Transition>
  </AppPaper>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-tickets-transport {
  h3 {
    margin: 0 0 0.75rem;
    font-family: $font-barlow;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.42857143;
    letter-spacing: 0.10714286em;
    text-transform: uppercase;
  }

  section {
    border: 1px solid $black;
    border-radius: 0.125rem;

    & > header {
      padding: 1rem;
      border-bottom: 1px solid $black;
    }
  }
}

.app-tickets-transport__direction {
  position: relative;

  p {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 3.375rem;
    margin: 0;
    padding: 0.75rem;
    color: $black;
    border-bottom: 1px solid $black;

    span,
    strong {
      display: block;
      font-family: $font-barlow;
      line-height: 1.25rem;
    }

    strong {
      margin-right: 0.5rem;
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: uppercase;
    }

    span {
      font-size: 0.875rem;
    }

    &:last-of-type {
      border-bottom: 0;
    }
  }

  .app-button-icon-rounded {
    background-color: $white;

    svg {
      width: 1.5rem;
      height: auto;
    }

    &:hover {
      background-color: $black;
    }
  }

  @include media-breakpoint-up(md) {
    p {
      strong {
        font-size: 0.875rem;
      }

      span {
        font-size: 1rem;
      }
    }
  }
}
</style>
